import {
  TIER_COLOURS,
  goalCumValue,
  goalLabel,
  goalSeries,
  mapTotals,
  money,
  num,
  pickedLevers,
  prioritise,
  type Goal,
  type GoalPriority,
  type ValueMap,
} from "@valuemap/core";
import { useEffect } from "react";
import logo from "./assets/salesfix-logo.svg";
import styles from "./Board.module.css";

/**
 * The Present board (reference `renderBoard`): every goal as a card, for the room and for print.
 * Wording and card content follow the reference tool. ADR-010 adds the totals strip, each goal's
 * priority (cards in priority order once Prioritise has values), a two-line stakeholder block and a
 * branded print frame. Anything added to the reference is marked `data-board-extra`.
 */

function boardDate(date: string): string {
  return date
    ? new Date(date + "T00:00").toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
}

/** A CSS string literal for user text (used in the print footer). */
function cssString(s: string): string {
  return `"${s
    .replace(/[\\"]/g, "\\$&")
    .replace(/[\r\n\f]+/g, " ")
    .replace(/</g, "\\3c ")}"`;
}

/** The custom property the `@page` footer in Board.module.css reads. */
const PRINT_FOOTER = "--vm-print-footer";

/** The printed footer: the map name and its date (or today's, when the map has none). */
function printFooter(map: ValueMap): string {
  const date =
    boardDate(map.org.date) ||
    new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  return cssString(["Value Map", map.org.name.trim(), date].filter(Boolean).join("  ·  "));
}

/**
 * Margin boxes inherit from the root element, so the footer text is set there. (An inline `<style>`
 * would be blocked by the Content Security Policy; setting a property through the CSSOM is not.)
 */
function usePrintFooter(map: ValueMap) {
  const footer = printFooter(map);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(PRINT_FOOTER, footer);
    return () => {
      root.style.removeProperty(PRINT_FOOTER);
    };
  }, [footer]);
}

function Row({ k, v }: { k: string; v: string }) {
  if (!v.trim()) return null;
  return (
    <div className={styles.row}>
      <div className={styles.k}>{k}</div>
      <div className={styles.v}>{v.trim()}</div>
    </div>
  );
}

/** Reference `goalValueLine`: shown only when the goal has a positive quantified value. */
function ValueLine({ goal, years }: { goal: Goal; years: number }) {
  const cum = goalCumValue(goal, years);
  if (cum <= 0) return null;
  const y1 = goalSeries(goal, years)[0] || 0;
  return (
    <div className={styles.valLine}>
      <span className={styles.valK}>Quantified value:</span> Year 1 {money(y1)} · {years}-year{" "}
      {money(cum)}
    </div>
  );
}

/** The map totals from Quantify, with the same wording. Shown once anything is quantified. */
function Totals({ map }: { map: ValueMap }) {
  const years = map.horizon;
  const t = mapTotals(map.goals, years);
  if (t.cumulativeExpected <= 0) return null;
  return (
    <div className={styles.totals} data-board-extra>
      <div className={styles.total}>
        <div className={styles.tK}>Year one value</div>
        <div className={styles.tV}>{money(t.year1)}</div>
      </div>
      <div className={`${styles.total} ${styles.hero}`}>
        <div className={styles.tK}>{years}-year value</div>
        <div className={styles.tV}>{money(t.cumulativeExpected)}</div>
        {t.cumulativeLow !== t.cumulativeHigh && (
          <div className={styles.tRange}>
            range {money(t.cumulativeLow)} to {money(t.cumulativeHigh)}
          </div>
        )}
      </div>
      {t.hoursPerYear > 0 && (
        <div className={`${styles.total} ${styles.alt}`}>
          <div className={styles.tK}>Hours freed / year</div>
          <div className={styles.tV}>{num(t.hoursPerYear, 0)}</div>
        </div>
      )}
    </div>
  );
}

function Card({
  goal,
  index,
  years,
  rank,
}: {
  goal: Goal;
  index: number;
  years: number;
  rank?: { position: number; priority: GoalPriority };
}) {
  const stakeholder = goal.stakeholder.trim();
  const personalWin = goal.personalWin.trim();
  const picked = pickedLevers(goal);
  return (
    <article className={styles.card} data-goal-index={index}>
      <div className={styles.cardHead}>
        {rank && (
          <span
            className={styles.rank}
            style={{ background: `#${TIER_COLOURS[rank.priority.tier.key]}` }}
            title={`Priority ${rank.position}`}
            data-board-extra
          >
            <span className={styles.srOnly}>Priority </span>
            {rank.position}
          </span>
        )}
        <h3>{goalLabel(goal, index)}</h3>
        {rank && (
          <span className={`${styles.tier} ${styles[rank.priority.tier.key]}`} data-board-extra>
            {rank.priority.tier.label}
          </span>
        )}
      </div>
      {stakeholder || personalWin ? (
        <div className={styles.who}>
          {stakeholder && <div className={styles.stakeholder}>{stakeholder}</div>}
          {personalWin && (
            <div className={styles.win}>
              <span className={styles.winK}>Personal win:</span> {personalWin}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.whoSpacer} />
      )}
      <div className={styles.rows}>
        <Row k="Right now" v={goal.rightNow} />
        <Row k="Objective" v={goal.objective} />
        <Row k="Capability" v={goal.capability} />
        <Row k="Outcome" v={goal.outcome} />
      </div>
      {picked.length > 0 && (
        <div className={styles.row}>
          <div className={styles.k}>Where the value comes from</div>
          <div className={styles.chips}>
            {picked.map((p, i) => (
              <span key={i} className={styles.vchip}>
                {p.name}
                {p.measure.trim() && <span className={styles.m}> {p.measure.trim()}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      <ValueLine goal={goal} years={years} />
      {goal.costInaction.trim() && (
        <div className={styles.coiLine}>
          <span className={styles.coiK}>Cost of inaction:</span> {goal.costInaction.trim()}
        </div>
      )}
    </article>
  );
}

export function Board({ map }: { map: ValueMap }) {
  const sub = [
    map.org.facilitator && "Facilitated by " + map.org.facilitator,
    boardDate(map.org.date),
  ]
    .filter(Boolean)
    .join("  ·  ");
  // Priority means something only once a goal has a value; until then keep the map order, as the
  // reference does.
  const result = prioritise(map.goals, map.horizon);
  const ranked = result.maxValue > 0;
  const order = ranked ? result.ranked : result.goals;
  usePrintFooter(map);
  return (
    <section className={styles.board}>
      <div className={`${styles.brand} print-only`} data-board-extra>
        <img src={logo} alt="SalesFix" width={118} height={31} />
      </div>
      <div className={styles.head}>
        <div className={styles.eyebrow}>Value Map</div>
        <h1>{map.org.name.trim() || "Value Map"}</h1>
        {sub && <div className={styles.sub}>{sub}</div>}
      </div>
      {map.goals.length === 0 ? (
        <div className={styles.empty}>No goals captured yet. Switch to Edit to build the map.</div>
      ) : (
        <>
          <Totals map={map} />
          <div className={styles.grid}>
            {order.map((p, position) => {
              const g = map.goals[p.index];
              if (!g) return null;
              return (
                <Card
                  key={g.id}
                  goal={g}
                  index={p.index}
                  years={map.horizon}
                  rank={ranked ? { position: position + 1, priority: p } : undefined}
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
